/**
 * チャンク分割録音機能
 * 2-5秒単位でBlobを生成し、IndexedDBに永続化
 */

import { detectOptimalCodec, getAudioConstraints, getRecordingQuality } from './audioRecorder.js'
import {
  detectOptimalVideoCodec,
  getVideoConstraints,
  VIDEO_RECORDING_LIMITS,
  createPortraitPreviewSession
} from './videoRecorder.js'

export class ChunkedRecorder {
  constructor(options = {}) {
    this.recordingId = options.recordingId || this.generateRecordingId()
    this.day = options.day
    this.type = options.type
    this.userId = options.userId
    this.learnerId = options.learnerId ?? null

    // フィードバック録音用パラメータ
    this.recordingMode = options.recordingMode // 'feedback' または undefined
    this.studentId = options.studentId // フィードバック対象の生徒ID
    this.submissionId = options.submissionId // 対象の提出ID

    // 'audio'（既存・デフォルト）または 'video'（フィードバック動画、新規）
    this.mediaKind = options.mediaKind === 'video' ? 'video' : 'audio'

    // コーデック設定（mediaKind === 'video' のときだけ動画用に分岐。既存の音声経路は無変更）
    if (this.mediaKind === 'video') {
      const videoCodec = detectOptimalVideoCodec()
      if (!videoCodec) {
        throw new Error('この端末は動画フィードバック録画に対応していません')
      }
      this.codecConfig = { ...videoCodec, codec: videoCodec.mimeType, type: 'webm' }
      this.qualityConfig = { timeslice: VIDEO_RECORDING_LIMITS.timeslice, maxDuration: VIDEO_RECORDING_LIMITS.maxDurationMs }
    } else {
      this.codecConfig = detectOptimalCodec()
      this.qualityConfig = getRecordingQuality(this.codecConfig.codec)
    }
    
    // 録音状態
    this.isRecording = false
    this.mediaRecorder = null
    this.stream = null
    // 動画のみ: 録画開始前にプレビュー確認・向き補正するためのセッション
    // （外部から先にprepareVideoPreview()で作られていればそれを再利用する）
    this.portraitSession = options.portraitSession || null
    // 動画のみ: 録画開始時点で確定した回転角度（サーバー側での補正に使う）
    this.rotationDeg = 0
    this.chunks = []
    this.chunkIndex = 0
    this.startTime = null
    this.recordingTime = 0
    
    // イベントハンドラー
    this.onChunk = options.onChunk || (() => {})
    this.onProgress = options.onProgress || (() => {})
    this.onError = options.onError || (() => {})
    this.onComplete = options.onComplete || (() => {})
    // 動画プレビュー表示用（getUserMedia成功直後にstreamを通知。音声では未使用）
    this.onStreamReady = options.onStreamReady || (() => {})
    
    // チャンク設定（qualityConfigから取得、オプションで上書き可能）
    this.timeslice = options.timeslice || this.qualityConfig.timeslice
    
    // タイマー
    this.timeInterval = null
    this.chunkInterval = null
  }
  
  /**
   * 録音IDを生成
   */
  generateRecordingId() {
    return `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
  
  /**
   * 動画プレビュー準備（録画開始前に呼ぶ。カメラ取得のみ行い、MediaRecorderはまだ起動しない。
   * 呼び出し側はこれで得たsessionで向き補正・確認ができる）
   * @param {number} initialRotationDeg
   */
  async prepareVideoPreview(initialRotationDeg = 0) {
    if (this.mediaKind !== 'video') {
      throw new Error('prepareVideoPreviewは動画録画時のみ使用できます')
    }
    if (this.portraitSession) {
      return this.portraitSession
    }
    const rawStream = await navigator.mediaDevices.getUserMedia(getVideoConstraints())
    this.portraitSession = await createPortraitPreviewSession(rawStream, initialRotationDeg)
    this.onStreamReady(this.portraitSession.stream)
    return this.portraitSession
  }

  /**
   * 録音開始
   */
  async start() {
    try {
      console.log('🎤 チャンク分割録音開始:', {
        recordingId: this.recordingId,
        codec: this.codecConfig.codec,
        timeslice: this.timeslice
      })

      if (this.mediaKind === 'video') {
        // prepareVideoPreview()が事前に呼ばれていなければここで用意する
        if (!this.portraitSession) {
          await this.prepareVideoPreview()
        }
        this.stream = this.portraitSession.stream
        // この時点で確定した回転角度を記録し、サーバー側の補正に使う
        this.rotationDeg = this.portraitSession.getRotation()
      } else {
        // 音声は既存どおり、録音開始と同時にストリーム取得
        this.stream = await navigator.mediaDevices.getUserMedia(getAudioConstraints())
        this.onStreamReady(this.stream)
      }

      // MediaRecorder初期化
      const recorderOptions = {
        mimeType: this.codecConfig.mimeType,
        audioBitsPerSecond: this.codecConfig.audioBitsPerSecond
      }
      if (this.mediaKind === 'video') {
        recorderOptions.videoBitsPerSecond = this.codecConfig.videoBitsPerSecond
      }
      this.mediaRecorder = new MediaRecorder(this.stream, recorderOptions)
      
      // イベントハンドラー設定
      this.setupEventHandlers()
      
      // チャンク分割録音開始
      this.mediaRecorder.start(this.timeslice)
      this.isRecording = true
      this.startTime = Date.now()
      this.chunkIndex = 0
      this.chunks = []
      
      // 時間カウント開始
      this.startTimeCounter()
      
      console.log('✅ 録音開始成功')
      
    } catch (error) {
      console.error('❌ 録音開始エラー:', error)
      this.onError(error)
      throw error
    }
  }
  
  /**
   * 録音停止
   */
  stop() {
    if (!this.isRecording || !this.mediaRecorder) {
      return
    }
    
    console.log('⏹️ 録音停止:', {
      recordingId: this.recordingId,
      totalChunks: this.chunks.length,
      duration: this.recordingTime
    })
    
    this.isRecording = false
    this.mediaRecorder.stop()
    this.stopTimeCounter()

    // ストリーム停止
    // 動画の場合はportraitSession.stop()で生カメラのトラックを解放する
    if (this.portraitSession) {
      this.portraitSession.stop()
      this.portraitSession = null
    } else if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
    }
    this.stream = null
  }
  
  /**
   * イベントハンドラー設定
   */
  setupEventHandlers() {
    // データ利用可能時（チャンク生成時）
    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.handleChunk(event.data)
      }
    }
    
    // 録音停止時
    this.mediaRecorder.onstop = () => {
      console.log('🛑 MediaRecorder停止:', {
        totalChunks: this.chunks.length,
        duration: this.recordingTime
      })
      this.handleRecordingComplete()
    }
    
    // エラー時
    this.mediaRecorder.onerror = (event) => {
      console.error('❌ MediaRecorderエラー:', event.error)
      this.onError(event.error)
    }
  }
  
  /**
   * チャンク処理
   */
  async handleChunk(chunkData) {
    // Proxyオブジェクトを安全にシリアライズ
    const safeDay = this.day ? (typeof this.day === 'object' ? (this.day.value || this.day.id || this.day.day || this.day) : this.day) : null
    const safeType = this.type ? (typeof this.type === 'object' ? (this.type.value || this.type.id || this.type.type || this.type) : this.type) : null
    const safeUserId = this.userId ? (typeof this.userId === 'object' ? (this.userId.value || this.userId.id || this.userId) : this.userId) : null
    
    const chunkInfo = {
      recordingId: this.recordingId,
      chunkIndex: this.chunkIndex++,
      timestamp: Date.now(),
      size: chunkData.size,
      mimeType: chunkData.type,
      day: safeDay,
      type: safeType,
      userId: safeUserId,
      learnerId: this.learnerId ?? null,
      isLastChunk: !this.isRecording,  // 録音停止後のチャンクは最後のチャンク
      codecType: this.codecConfig.type, // 🆕 WebM/MP4分岐用
      // フィードバック録音用パラメータ
      recordingMode: this.recordingMode,
      studentId: this.studentId,
      submissionId: this.submissionId,
      mediaKind: this.mediaKind, // 'audio' または 'video'
      rotationDeg: this.mediaKind === 'video' ? this.rotationDeg : undefined // 動画のみ: サーバー側補正用の確定角度
    }
    
    console.log('📦 チャンク生成:', chunkInfo)
    
    // チャンクを配列に追加
    this.chunks.push({
      data: chunkData,
      info: chunkInfo
    })
    
    // チャンクイベント発火
    this.onChunk(chunkData, chunkInfo)
    
    // 進捗更新
    this.onProgress({
      chunkIndex: this.chunkIndex,
      totalChunks: this.chunks.length,
      duration: this.recordingTime,
      recordingId: this.recordingId
    })
  }
  
  /**
   * 録音完了処理
   */
  handleRecordingComplete() {
    const recordingInfo = {
      recordingId: this.recordingId,
      day: this.day,
      type: this.type,
      userId: this.userId,
      totalChunks: this.chunks.length,
      duration: this.recordingTime,
      codec: this.codecConfig.codec,
      completedAt: Date.now()
    }
    
    console.log('✅ 録音完了:', recordingInfo)
    
    // 完了イベント発火
    this.onComplete(recordingInfo, this.chunks)
  }
  
  /**
   * 時間カウント開始
   */
  startTimeCounter() {
    this.timeInterval = setInterval(() => {
      this.recordingTime = Math.floor((Date.now() - this.startTime) / 1000)
    }, 1000)
  }
  
  /**
   * 時間カウント停止
   */
  stopTimeCounter() {
    if (this.timeInterval) {
      clearInterval(this.timeInterval)
      this.timeInterval = null
    }
  }
  
  /**
   * リソース解放
   */
  destroy() {
    this.stop()

    // プレビュー確認中（録画未開始）にキャンセルされた場合、stop()は何もしないため
    // ここでportraitSessionが残っていればカメラを解放する
    if (this.portraitSession) {
      this.portraitSession.stop()
      this.portraitSession = null
    }

    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }

    if (this.chunkInterval) {
      clearInterval(this.chunkInterval)
    }

    this.chunks = []
    this.mediaRecorder = null
    this.stream = null
  }
  
  /**
   * 録音状態取得
   */
  getStatus() {
    return {
      isRecording: this.isRecording,
      recordingId: this.recordingId,
      chunkCount: this.chunks.length,
      duration: this.recordingTime,
      codec: this.codecConfig.codec
    }
  }
}
